import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import toast from 'react-hot-toast';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import useDeviceFingerprint from '../../../hooks/useDeviceFingerprint';
import '../design/member.css';

const MemberLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { member, requestOtp, verifyOtp } = useMemberAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { deviceId, loading: deviceLoading } = useDeviceFingerprint();

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (member) {
      navigate('/app', { replace: true });
    }
  }, [member, navigate]);

  const handleSendOtp = async () => {
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      // PHASE 2: Execute reCAPTCHA
      let recaptchaToken: string | undefined;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('member_otp_request');
          console.log('[Security] reCAPTCHA token generated');
        } catch (captchaError) {
          console.error('[Security] reCAPTCHA failed:', captchaError);
          // Continue without CAPTCHA in development
          if (import.meta.env.MODE === 'production') {
            throw new Error('Security verification failed. Please try again.');
          }
        }
      }

      // Request OTP with CAPTCHA token
      await requestOtp(phone.trim(), recaptchaToken);
      setStep('otp');
      toast.success('OTP sent to WhatsApp');
    } catch (error: any) {
      // Handle rate limiting errors
      if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.retryAfter || '15 minutes';
        toast.error(`Too many attempts. Please try again in ${retryAfter}`);
      } else {
        toast.error(error.message || 'Failed to send OTP');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      // PHASE 2: Execute reCAPTCHA
      let recaptchaToken: string | undefined;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('member_otp_verify');
          console.log('[Security] reCAPTCHA token generated for verification');
        } catch (captchaError) {
          console.error('[Security] reCAPTCHA failed:', captchaError);
          if (import.meta.env.MODE === 'production') {
            throw new Error('Security verification failed. Please try again.');
          }
        }
      }

      // PHASE 3: Include device fingerprint
      const fingerprintId = deviceId || undefined;
      if (fingerprintId) {
        console.log('[Security] Sending device fingerprint');
      }

      // Verify OTP with CAPTCHA token and device ID
      await verifyOtp(phone.trim(), otp.trim(), recaptchaToken, fingerprintId);
      toast.success('Welcome back!');
      navigate('/app', { replace: true });
    } catch (error: any) {
      // Handle specific error cases
      if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.retryAfter || '15 minutes';
        toast.error(`Too many attempts. Account locked for ${retryAfter}`);
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.error || 'Invalid OTP');
      } else {
        toast.error(error.message || 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // Security indicator
  const showSecurityBadge = import.meta.env.VITE_RECAPTCHA_SITE_KEY && deviceId;

  return (
    <div className="mapp" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 20px' }}>
      <div className="col" style={{ width: '100%', maxWidth: 380, gap: 22 }}>
        <div className="col" style={{ alignItems: 'center', gap: 8 }}>
          <span className="gympe-pill">
            Powered by <span className="gympe">Gym<b>pe</b></span>
          </span>
          <div className="dp dp-l" style={{ marginTop: 8 }}>Member login</div>
          <div className="sub">Enter your phone number to get a WhatsApp OTP</div>
        </div>

        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 20 }}>
          <div className="col" style={{ gap: 6 }}>
            <span className="lbl">Phone number</span>
            <div className="row" style={{ gap: 0, background: 'var(--card)', border: '1px solid var(--line-2)', borderRadius: 'var(--r-s)' }}>
              <span className="mid" style={{ padding: '12px 12px', borderRight: '1px solid var(--line)', color: 'var(--ink-2)' }}>+91</span>
              <input
                type="tel"
                inputMode="numeric"
                pattern="\d*"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                onKeyDown={e => e.key === 'Enter' && step === 'phone' && handleSendOtp()}
                placeholder="9876543210"
                disabled={step === 'otp'}
                autoFocus={step === 'phone'}
                style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', font: 'inherit', fontSize: 15, fontWeight: 600, padding: '12px 14px', color: 'var(--ink)' }}
              />
            </div>
          </div>

          {step === 'otp' && (
            <div className="col" style={{ gap: 6 }}>
              <span className="lbl">Enter OTP</span>
              <input
                className="minput num"
                type="tel"
                inputMode="numeric"
                pattern="\d*"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
                placeholder="000000"
                autoFocus
                style={{ textAlign: 'center', letterSpacing: '.5em', fontSize: 22, fontWeight: 700 }}
              />
            </div>
          )}

          {step === 'phone' ? (
            <button className="btn gold block" onClick={handleSendOtp} disabled={loading || phone.length !== 10}>
              {loading ? 'Sending OTP…' : 'Send OTP via WhatsApp'}
            </button>
          ) : (
            <div className="col" style={{ gap: 10 }}>
              <button className="btn gold block" onClick={handleVerifyOtp} disabled={loading || otp.length !== 6}>
                {loading ? 'Verifying…' : 'Verify & log in'}
              </button>
              <button
                className="btn ghost block"
                disabled={loading}
                onClick={() => {
                  setStep('phone');
                  setOtp('');
                }}
              >
                Change phone number
              </button>
            </div>
          )}

          {showSecurityBadge && (
            <div className="row" style={{ justifyContent: 'center', gap: 6, paddingTop: 4, borderTop: '1px solid var(--line)' }}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3.5l7 2.5v5.5c0 4.5-3.2 7.5-7 9-3.8-1.5-7-4.5-7-9V6z" />
              </svg>
              <span className="tiny">Protected</span>
            </div>
          )}
          {deviceLoading && <div className="tiny" style={{ textAlign: 'center' }}>Initializing security…</div>}
        </div>

        <div className="col" style={{ alignItems: 'center', gap: 4, textAlign: 'center' }}>
          <span className="tiny wrap">By logging in, you agree to our Terms of Service and Privacy Policy.</span>
          <span className="tiny wrap">Protected by reCAPTCHA and device verification.</span>
        </div>
      </div>
    </div>
  );
};

export default MemberLoginPage;
